import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import { DigitalFilesI, DigitalFilesService, SpecificDigitalFileI } from './digital-files.service';

import { EN } from 'app/core/translator/EN';
import { ActivatedRoute } from '@angular/router';
import {DPAMFile, DPAMRequestI, GroupAttributeI, ProductSetupService, ViewLevel} from '../../services/product-setup.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { AppConfig } from '../../../app.config';
import { MaxImageSizeModalComponent } from '../../modals/max-image-size-modal/max-image-size-modal.component';
import { InvalidUploadFileTypeModalComponent } from '../../modals/invalid-upload-file-type-modal/invalid-upload-file-type-modal.component';
import {mergeMap, tap} from 'rxjs/operators';
import {combineLatest, of} from 'rxjs';


@UntilDestroy()
@Component({
  selector: 'app-digital-files',
  templateUrl: './digital-files.component.html',
  styleUrls: ['./digital-files.component.scss']
})

export class DigitalFilesComponent implements OnInit, OnChanges {
  EN = EN;
  isZoomIn = false;

  @ViewChild('download', { static: false }) download: ElementRef;
  @ViewChild('inputFile', ) myInputVariable: ElementRef;

  @Input() data: DPAMFile[];
  @Input() dpamRequest: DPAMRequestI;
  @Input() fullView = true;
  @Input() confirmationView = false;
  @Input() fileGroup: 'original' | 'final';
  @Input() editorEnable = false;
  @Input() groupAttributes: GroupAttributeI[];
  @Input() viewLevel: ViewLevel;
  @Input() groupProduct: {dataPath: string, request: DPAMRequestI};
  @Input() isActionsDisabled = false;
  @Output() addSpecificComment: EventEmitter<string> = new EventEmitter();

  activeFile: {item: DPAMFile, type: string};
  activeFileType: string;
  digitalFiles: any;
  prominentFiles: any = ['DIGITALFILE_A_IMG_PRD_300_190','DIGITALFILE_A_IMG_PRD_200_283','DIGITALFILE_A_IMG_PRD_1500_1500','DIGITALFILE_A_IMG_HDR_670_123','DIGITALFILE_A_IMG_FTR_670_91'];

  constructor(private digitalFilesService: DigitalFilesService,
              private productService: ProductSetupService,
              private toasterService: ToastrService,
              private route: ActivatedRoute,
              protected appConfig: AppConfig,
              private dialog: MatDialog ) { }

  ngOnInit() {
    this.digitalFiles = this.digitalFilesService.createNodesTree(this.data, this.fileGroup, this.groupAttributes, this.viewLevel, this.dpamRequest.deliveryChannels[0].channelType);
    this.activeFile = this.digitalFiles[0];
    this.activeFileType = this.digitalFiles[0]?.type;
  }

  public onShowImagePreview(activeFile: any) {
    this.activeFile = activeFile.data;
    this.activeFileType = activeFile.data.type;
  }

  private onSuccessGetImage(data: SpecificDigitalFileI) {
    this.digitalFilesService.digitalFilesSource.next(Object.assign({}, this.digitalFiles, {
      digitalFilesMap: Object.assign({}, this.digitalFiles.digitalFilesMap, {
        [this.digitalFiles.activeImageType.fileIds[0]]: {
          fileContent: 'data:' + data.mimeType + ';base64,' + data.fileContent,
          fileName: data.fileName,
          mimeType: data.mimeType,
          fileSizeInKB: data.fileSizeInKB
        }
      }),
      activeImagePreview: 'data:' + data.mimeType + ';base64,' + data.fileContent,
      activeImageName: data.fileName
    }));
  }

  private onErrorGetImage() {
    console.log('onErrorGetImage');
  }


  onUpload(files: any[]) {
    if (!this.checkFileType(files[0])) {
      this.resetInput();
      return;
    }

    if (!this.checkImageSize(files[0])) {
      this.resetInput();
      return;
    }

    switch (this.viewLevel) {
      case ViewLevel.Group:
        this.uploadForGroup(files);
      break;
      case ViewLevel.Product:
        const processInstanceId = this.groupProduct.request.processInstanceId;
        this.uploadForProductInGroup(files, this.groupProduct.dataPath, processInstanceId)
          .pipe(
            mergeMap(() => this.productService.getSelectedProduct(this.groupProduct.dataPath))
          )
          .subscribe((res: DigitalFilesI[]) => {
          this.resetInput();
        }, () => {
          this.resetInput();
        });
      break;
      default:
        this.uploadForSingleProduct(files);
    }
  }

  onRemove() {

    switch (this.viewLevel) {
      case ViewLevel.Group:
        this.onRemoveForGroup();
        break;
      case ViewLevel.Product:
        this.onRemoveForProductInGroup(this.groupProduct.dataPath, this.groupProduct.request.processInstanceId)
          .pipe(
            mergeMap(() => this.productService.getSelectedProduct(this.groupProduct.dataPath))
          )
          .subscribe(() => {
            this.toasterService.success('Successfully deleted');
          });
        break;
      default:
        this.onRemoveForSingleProduct();
    }
  }

  checkFileType(event: HTMLInputElement): boolean {
    if (!(this.appConfig.data.uploadFileContentType.includes(event.type) || event.type.includes('image/') || event.type.includes('video/'))) {
      this.dialog.open(InvalidUploadFileTypeModalComponent);
      return false;
    }
    return true;
  }

  checkImageSize(event: HTMLInputElement): boolean {
    if (event.size > this.appConfig.data.maxImageSize) {
      this.dialog.open(MaxImageSizeModalComponent);
      return false;
    }

    return true;
  }

  onDownload() {
    const imgUrl = this.activeFile.item.fileContent;
    const imgName = this.activeFile.item.fileName;
    this.digitalFilesService.downloadImage(imgUrl, imgName);
  }

  onZoomIn() {
    if (this.activeFile.item) {
      this.isZoomIn = !this.isZoomIn;
    }
  }

  get isEditable() {
   return !this.isActionsDisabled;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && changes['data']['currentValue']) {
      this.resetInput();
      this.digitalFiles = this.digitalFilesService.createNodesTree(this.data, this.fileGroup, this.groupAttributes, this.viewLevel, this.dpamRequest.deliveryChannels[0].channelType);
      if (this.activeFileType) {
        this.activeFile = this.digitalFiles.find(f => f.type === this.activeFileType);
        return;
      }
      this.activeFile = this.digitalFiles[0];
      this.activeFileType = this.digitalFiles[0]?.type;
    }
  }

  private resetInput() {
    if (this.myInputVariable?.nativeElement) {
      this.myInputVariable.nativeElement.value = '';
    }
  }

  private uploadForGroup(files: any[]) {
    combineLatest(this.productService.productState
      .groupData.data
      .map((p) => {
        return this.uploadForProductInGroup(files, p.dataPath, p.request.processInstanceId);
      }))
      .pipe(
        mergeMap(() => this.productService.getSelectedProduct(this.groupProduct.dataPath)))
      .subscribe((res: DigitalFilesI[]) => {
      this.resetInput();
    }, () => {
      this.resetInput();
    });
  }

  private onRemoveForGroup() {
    combineLatest(this.productService.productState
      .groupData.data
      .map((p) => {
        return this.onRemoveForProductInGroup(p.dataPath, p.request.processInstanceId);
      }))
      .pipe(
        untilDestroyed(this),
        mergeMap(() => this.productService.getSelectedProduct(this.groupProduct.dataPath))
        )
      .subscribe(() => {
      this.toasterService.success('Successfully deleted');
    });
  }

  private uploadForProductInGroup(files: any[], path: string, processInstanceId: number) {
    const url = new URL(path);

    const upcId = url.pathname.replace('upc-data.json', '')
      .split('/')[2]
      .replace('%2F', '/')
      .replace('%2F', '');
    const token = window.location.href.split('?')[1];
    const isoCode = this.dpamRequest.languageVersion.language.isoCode;
    const previousActiveFile = this.activeFile;

    const upload$ = this.digitalFilesService
      .uploadGroupProductImage(upcId, processInstanceId, this.fileGroup, isoCode, this.activeFileType, files[0], token);

    return upload$
      .pipe(
        untilDestroyed(this),
        mergeMap(() => {
          if (previousActiveFile.item) {
            return this.digitalFilesService
              .deleteGroupProductImage(
                upcId,
                processInstanceId,
                this.fileGroup,
                isoCode,
                previousActiveFile.type,
                previousActiveFile.item.fileName,
                token
              );
          }
          return of();
        })
      );
  }

  private onRemoveForProductInGroup(path: string, processInstanceId) {
    const url = new URL(path);

    const upcId = url.pathname.replace('upc-data.json', '')
      .split('/')[2]
      .replace('%2F', '/')
      .replace('%2F', '');
    const token = window.location.href.split('?')[1];
    const isoCode = this.dpamRequest.languageVersion.language.isoCode;

    return this.digitalFilesService
      .deleteGroupProductImage(upcId, processInstanceId, this.fileGroup, isoCode, this.activeFileType, this.activeFile.item.fileName, token)
      .pipe(
        untilDestroyed(this),
      );
  }

  private uploadForSingleProduct(files: any[]) {
    const upcId = this.route.snapshot.params['upcId'];
    const token = window.location.href.split('?')[1];
    const isoCode = this.dpamRequest.languageVersion.language.isoCode;
    const processInstanceId = this.dpamRequest.processInstanceId;
    const previousActiveFile = this.activeFile;

    const upload$ = this.digitalFilesService
      .uploadImage(upcId, processInstanceId, this.fileGroup, isoCode, this.activeFileType, files[0], token);

    upload$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          if (previousActiveFile.item) {
            this.digitalFilesService
              .deleteImage(
                upcId,
                processInstanceId,
                this.fileGroup,
                isoCode,
                previousActiveFile.type,
                previousActiveFile.item.fileName,
                token
              )
              .subscribe();
          }
        })
      )
      .subscribe((res: DigitalFilesI[]) => {
        // If new image upload, set new fileIds for activeImageType;
        this.resetInput();
      }, () => {
        this.resetInput();
      });
  }

  private onRemoveForSingleProduct() {
    const upcId = this.route.snapshot.params['upcId'];
    const token = window.location.href.split('?')[1];
    const isoCode = this.dpamRequest.languageVersion.language.isoCode;
    const processInstanceId = this.dpamRequest.processInstanceId;

    this.digitalFilesService
      .deleteImage(upcId, processInstanceId, this.fileGroup, isoCode, this.activeFileType, this.activeFile.item.fileName, token)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.toasterService.success('Successfully deleted');
      });
  }

}
